import { createContext, useContext, useState, useEffect } from 'react';
import axios from '../utils/axios';
import { getSettingsImage } from '../utils/imageHelpers';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

// Cache için global değişken
let themeCache = null;
let themeCacheTimestamp = null;
const THEME_CACHE_DURATION = 10 * 60 * 1000; // 10 dakika

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState({
    primaryColor: '#C5A686',
    secondaryColor: '#000000',
    accentColor: '#ffffff'
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTheme();
  }, []);

  const fetchTheme = async () => {
    try {
      // Cache kontrolü
      const now = Date.now();
      if (themeCache && themeCacheTimestamp && (now - themeCacheTimestamp) < THEME_CACHE_DURATION) {
        setTheme(themeCache.colors);
        applyTheme(themeCache.colors);
        applySiteSettings(themeCache.settings);
        setLoading(false);
        return;
      }

      const response = await axios.get('/settings');
      const settings = response.data?.data || response.data;
      
      if (settings && settings.themeColors) {
        const themeColors = {
          primaryColor: settings.themeColors.primaryColor || '#C5A686',
          secondaryColor: settings.themeColors.secondaryColor || '#000000',
          accentColor: settings.themeColors.accentColor || '#ffffff'
        };
        setTheme(themeColors);
        applyTheme(themeColors);
        applySiteSettings(settings);
        
        // Cache'e kaydet
        themeCache = { colors: themeColors, settings };
        themeCacheTimestamp = now;
      } else {
        // Apply default theme
        applyTheme(theme);
        applySiteSettings(settings);
      }
    } catch (error) {
      console.error('Tema yüklenemedi:', error);
      // Apply default theme on error
      applyTheme(theme);
    } finally {
      setLoading(false);
    }
  };

  const applySiteSettings = (settings) => {
    if (!settings) return;
    
    // Update document title (SEO Title kullan, yoksa Site Adı)
    document.title = settings.seoTitle || settings.siteName || 'Mashow';
    
    // Update favicon
    const faviconUrl = settings.favicon ? getSettingsImage(settings.favicon) : '/vite.svg';
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = faviconUrl;
    
    // Update meta tags
    if (settings.seoDescription) {
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = 'description';
        document.head.appendChild(meta);
      }
      meta.content = settings.seoDescription;
    }
  };

  const applyTheme = (themeColors) => {
    const root = document.documentElement;
    
    // Convert hex to RGB for Tailwind
    const hexToRgb = (hex) => {
      if (!hex) return '197 166 134'; // default accent color
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? `${parseInt(result[1], 16)} ${parseInt(result[2], 16)} ${parseInt(result[3], 16)}`
        : '197 166 134';
    };

    // Apply CSS variables
    root.style.setProperty('--color-primary', hexToRgb(themeColors.primaryColor));
    root.style.setProperty('--color-secondary', hexToRgb(themeColors.secondaryColor));
    root.style.setProperty('--color-accent', hexToRgb(themeColors.accentColor));
    
    // Also set the actual color values (hex)
    root.style.setProperty('--color-primary-hex', themeColors.primaryColor || '#C5A686');
    root.style.setProperty('--color-secondary-hex', themeColors.secondaryColor || '#000000');
    root.style.setProperty('--color-accent-hex', themeColors.accentColor || '#ffffff');
  };

  const updateTheme = (newTheme) => {
    setTheme(newTheme);
    applyTheme(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, updateTheme, loading }}>
      {children}
    </ThemeContext.Provider>
  );
};

